import React, { type ReactElement, useEffect, useState } from 'react';
import { faPersonWalkingArrowLoopLeft } from '@fortawesome/free-solid-svg-icons';
import TableActions from 'atom-components/table-actions-lite';
import DeallocationResultsModal from 'pages/marking/components/deallocation-results-modal';
import { isAnActiveMarkerOfResponse } from 'pages/marking/helpers/marker.helpers';
import { marksOfResponseParams } from 'pages/marking/helpers/request-params.helpers';
import ReallocateResponseModal from 'pages/marking/subpages/marking-allocation-tab//reallocate-response-modal';
import type { ChangeMarkerMark } from 'pages/marking/types';
import MarkingTaskPolicy from 'policies/marking-task.policy';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { useTestInstanceResponseMarks } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import { usePageAccess } from 'services/providers/page-access.provider';

type Props = {
  responseId: number;
  allocatedMarkerId: number;
  allocatedOrganisationUser: BaseCustomerUser | undefined;
  isLoading: boolean;
  onReallocate: () => void;
};

export default function ReallocateResponseButton({
  responseId,
  allocatedMarkerId,
  allocatedOrganisationUser,
  isLoading,
  onReallocate
}: Props): ReactElement {
  const [isDeterminingWhichModal, setIsDeterminingWhichModal] = useState<boolean>(false);
  const [isReallocateResponseModalOpen, setIsReallocateResponseModalOpen] =
    useState<boolean>(false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState<boolean>(false);
  const { authorisableUser } = usePageAccess();

  // Fetch marks and mark items linked to the response to determine if we can deallocate the marker.
  const marksQuery = useTestInstanceResponseMarks<ChangeMarkerMark>(
    marksOfResponseParams(responseId),
    // Only run this query when the Reallocate Response button is clicked for performance reasons:
    { enabled: isDeterminingWhichModal }
  );

  useEffect(() => {
    if (isDeterminingWhichModal && marksQuery.isSuccess) {
      if (isAnActiveMarkerOfResponse(allocatedMarkerId, marksQuery.data?.json)) {
        setIsResultsModalOpen(true);
      } else {
        setIsReallocateResponseModalOpen(true);
      }
      setIsDeterminingWhichModal(false);
    }
  }, [isDeterminingWhichModal, allocatedMarkerId, marksQuery.data?.json, marksQuery.isSuccess]);

  return (
    <>
      <ReallocateResponseModal
        isOpen={isReallocateResponseModalOpen}
        onClose={(): void => setIsReallocateResponseModalOpen(false)}
        onReallocate={onReallocate}
        responseId={responseId}
        allocatedMarkerId={allocatedMarkerId}
        allocatedOrganisationUser={allocatedOrganisationUser}
        isLoading={isLoading}
      />
      <DeallocationResultsModal
        isOpen={isResultsModalOpen}
        onClose={(): void => setIsResultsModalOpen(false)}
        responseId={responseId}
        allocatedMarkerId={allocatedMarkerId}
        allocatedOrganisationUser={allocatedOrganisationUser}
        onForceReallocate={onReallocate}
      />
      {MarkingTaskPolicy.administrate(authorisableUser) && (
        <TableActions.Button
          tooltip="Reallocate Response"
          onClick={(): void => setIsDeterminingWhichModal(true)}
          icon={faPersonWalkingArrowLoopLeft}
          disabled={isDeterminingWhichModal}
        />
      )}
    </>
  );
}
